import { datevPaymentTranslations } from './datev-payments-i18n';
import { centralMailOutboundTranslations } from './central-mail-outbound-i18n';
import { mcpTranslations } from './mcp-i18n';
import { mailSettingsTranslations } from './mail-settings-i18n';
import { addressTypeTranslations } from './address-type-i18n';

import { conditionsTranslations } from './conditions-i18n';

import { productUnitsTranslations } from './product-units-i18n';
import i18n, { type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  DEFAULT_LOCALE,
  inventoryOverviewLabels,
  priceCalculationLabels,
  paymentPlanLabels,
  crmRecentActionsLabels,
  ledgerAdoptionTranslations,
  ledgerClosingLabels,
  ledgerAnalysisLabels,
  cashbookLabels,
  fiscalFxLabels,
  vatReturnLabels,
  bankProviderReviewLabels,
  supplierCreditLabels,
  mailWorkflowLabels,
  mailLifecycleLabels,
  FEATURE_CATALOGUE_BY_KEY,
  SUPPORTED_LOCALES,
  isSupportedLocale,
  localeDirection,
  type SupportedLocale,
} from '@erp/shared';
import { de } from './de';
import { en } from './en';
import { es } from './es';
import { fr } from './fr';
import { tr } from './tr';
import { documentRoutingTranslations } from './document-routing-i18n';
import { frenchIdentifiersTranslations } from './french-identifiers-i18n';
import { logisticsOperationsTranslations } from './logistics-operations-i18n';
import { returnsTranslations } from './returns-i18n';
import { containerTranslations } from './reusable-containers-i18n';
import { electronicInvoiceTranslations } from './electronic-invoices-i18n';
import { deliveryToursTranslations } from './delivery-tours-i18n';
import { operationalSetupTranslations } from './operational-setup-i18n';
import { operationsAlertsTranslations } from './operations-alerts-i18n';
import { legacyDocumentTranslations } from './legacy-documents-i18n';
import { catalogReadinessTranslations } from './catalog-readiness-i18n';
import { migrationTranslations } from './migration-i18n';
import { portableImportTranslations } from './portable-import-i18n';
import { warehouseImportTranslations } from './warehouse-import-i18n';

import { customFieldUsabilityTranslations } from './custom-field-usability-i18n';
import { partnerUsabilityTranslations } from './partner-usability-i18n';
import { settingsUsabilityTranslations } from './settings-usability-i18n';
import { centralMailTranslations } from './central-mail-i18n';
import { centralMailMessageTranslations } from './central-mail-message-i18n';
import { centralMailHistoryTranslations } from './central-mail-history-i18n';
import { centralMailSyncTranslations } from './central-mail-sync-i18n';
import { centralMailAssignmentTranslations } from './central-mail-assignment-i18n';

export const LOCALE_STORAGE_KEY = 'erp-locale';

function detectLocale(): SupportedLocale {
  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored !== null && isSupportedLocale(stored)) {
    return stored;
  }

  const browserLocale = window.navigator.language.slice(0, 2);
  return isSupportedLocale(browserLocale) ? browserLocale : DEFAULT_LOCALE;
}

const initialLocale = detectLocale();
document.documentElement.lang = initialLocale;
document.documentElement.dir = localeDirection(initialLocale);

// Locale dictionaries have the same runtime structure. Keeping this merge
// shallow avoids distributing every literal translation through a five-locale
// union while TypeScript checks i18next's resource overloads.
interface BaseLocaleResources {
  translation: Record<string, unknown> & {
    roles: Record<string, unknown> & { resources: Record<string, string> };
  };
}
const baseResources: Record<SupportedLocale, BaseLocaleResources> = { tr, fr, es, de, en };
const resourceFeatures = {
  RETURNS: 'complaints_returns',
  PICKING: 'scanner_picking',
  INVENTORY_COUNTS: 'full_inventory',
  DELIVERY_TOURS: 'delivery_tours',
  REUSABLE_CONTAINERS: 'reusable_containers',
  ELECTRONIC_INVOICES: 'electronic_invoices',
} as const;
const resources: Resource = Object.fromEntries(
  SUPPORTED_LOCALES.map((locale) => [
    locale,
    {
      ...baseResources[locale],
      translation: {
        ...baseResources[locale].translation,
        mcp: mcpTranslations[locale],
        documentRouting: documentRoutingTranslations[locale],
        datevPayments: datevPaymentTranslations[locale],
        paymentPlans: paymentPlanLabels[locale],
        ledgerAdoption: ledgerAdoptionTranslations[locale],
        ledgerClosing: ledgerClosingLabels[locale],
        ledgerAnalysis: ledgerAnalysisLabels[locale],
        ledgerCashbook: cashbookLabels[locale],
        fiscalFx: fiscalFxLabels[locale],
        vatReturn: vatReturnLabels[locale],
        bankProviderReview: bankProviderReviewLabels[locale],
        supplierCredits: supplierCreditLabels[locale],
        frenchIdentifiers: frenchIdentifiersTranslations[locale],
        addressTypes: addressTypeTranslations[locale],
        settingsUsability: settingsUsabilityTranslations[locale],
        conditions: conditionsTranslations[locale],
        productUnits: productUnitsTranslations[locale],
        partnerUsability: partnerUsabilityTranslations[locale],
        customFieldUsability: customFieldUsabilityTranslations[locale],
        ...logisticsOperationsTranslations[locale],
        returns: returnsTranslations[locale],
        containers: containerTranslations[locale],
        ...electronicInvoiceTranslations[locale],
        ...deliveryToursTranslations[locale],
        ...operationalSetupTranslations[locale],
        operationsAlerts: operationsAlertsTranslations[locale],
        mailSettings: mailSettingsTranslations[locale],
        mail: {
          ...centralMailTranslations[locale],
          messages: centralMailMessageTranslations[locale].messages,
          history: centralMailHistoryTranslations[locale],
          sync: centralMailSyncTranslations[locale],
          assignment: centralMailAssignmentTranslations[locale],
          outbound: centralMailOutboundTranslations[locale],
          workflow: mailWorkflowLabels[locale],
          lifecycle: mailLifecycleLabels[locale],
          errors: {
            ...centralMailTranslations[locale].errors,
            ...centralMailMessageTranslations[locale].errors,
          },
          validation: {
            ...centralMailTranslations[locale].validation,
            ...centralMailMessageTranslations[locale].validation,
          },
        },
        legacy: legacyDocumentTranslations[locale],
        migration: migrationTranslations[locale],
        portableImport: portableImportTranslations[locale],
        warehouseImport: warehouseImportTranslations[locale],
        inventoryOverview: inventoryOverviewLabels[locale],
        pricing: priceCalculationLabels[locale],
        crmRecentActions: crmRecentActionsLabels[locale],
        catalogReadiness: catalogReadinessTranslations[locale],
        roles: {
          ...baseResources[locale].translation.roles,
          resources: {
            ...baseResources[locale].translation.roles.resources,
            OPERATIONS: operationsAlertsTranslations[locale].title,
            MAIL_ACCOUNTS: centralMailTranslations[locale].accounts.title,
            MAIL_MESSAGES: centralMailTranslations[locale].workspaceTitle,
            MAIL_RECORDS: centralMailTranslations[locale].accounts.sharing,
            PRICING: priceCalculationLabels[locale].title,
            ...Object.fromEntries(
              Object.entries(resourceFeatures).map(([resource, feature]) => [
                resource,
                FEATURE_CATALOGUE_BY_KEY.get(feature)!.name[locale],
              ]),
            ),
          },
        },
      },
    },
  ]),
);

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLocale,
  fallbackLng: 'en',
  supportedLngs: [...SUPPORTED_LOCALES],
  interpolation: { escapeValue: false },
  returnNull: false,
});

export function changeLocale(locale: SupportedLocale): void {
  window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  document.documentElement.lang = locale;
  document.documentElement.dir = localeDirection(locale);
  void i18n.changeLanguage(locale);
}

export { i18n };
